/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

const serverUrls = {
  suite: {
    development: 'api-dev.friendli.ai',
    staging: 'suite-staging.friendli.ai',
    production: 'suite.friendli.ai',
  },
  backoffice: {
    localhost: 'api-dev.friendli.ai',
    staging: 'backoffice-staging.friendli.ai',
    production: 'backoffice.friendli.ai',
  },
};

enum ApiPaths {
  Login = 'api/auth/login',
  SignOut = 'api/auth/signout',

  SelfSignUp = 'api/auth/signup/register-self',
  ConfirmSelfSignUp = 'api/auth/signup/register-self/confirm',
  SendSignUpVerificationEmail = 'api/auth/signup/register-self/verify-email/send',
  GetInvitationDetail = 'api/auth/signup/register-invite/detail',
  ConfirmSignUpInvitation = 'api/auth/signup/register-invite/confirm',
  ConfirmLoginInvitation = 'api/auth/login/invite',
  AcceptInvitation = 'api/auth/login/invite/accept',
  SendResetPasswordEmail = 'api/auth/password-reset/email',
  ConfirmResetPassword = 'api/auth/password-reset/confirm',

  // AWS
  AWSSubscription = 'api/auth/register/aws-marketplace',
  AWSSignUp = 'api/auth/register/submit-signup',
  AWSSubmitEnlist = 'api/auth/register/submit-enlist',
  AWSResendEmail = 'api/auth/register/resend-email',
}

const base = {
  // API server path
  GRAPHQL_PATH: '/api/graphql',
  AUTH_PATH: '/api/auth',

  // Website path
  LOGIN_PATH: '/login',
};

const getApiEnv = (baseUrl: string) => ({
  // API server URL
  SERVER_URL: `https://${baseUrl}`,
  WS_URL: `wss://${baseUrl}`,
  ...base,
});

function isApiPath(path: string): path is ApiPaths {
  return Object.values(ApiPaths).includes(path as ApiPaths);
}

const friendliBaseUrl = 'https://friendli.ai';

enum FriendliWebsiteUrls {
  Default = friendliBaseUrl,

  Policy = `${friendliBaseUrl}/privacypolicy`,
  Terms = `${friendliBaseUrl}/terms-of-service`,
  DedicatedEndpointsPolicy = `${friendliBaseUrl}/dedicated-endpoints-privacypolicy`,
  DedicatedEndpointsTerms = `${friendliBaseUrl}/dedicated-endpoints-terms-of-service`,

  Service = `${friendliBaseUrl}/service`,
  Patents = `${friendliBaseUrl}/patents`,
  Pricing = `${friendliBaseUrl}/pricing`,

  Contact = `${friendliBaseUrl}/contact`,

  ServerlessEndpoints = `${friendliBaseUrl}/products/serverless-endpoints`,
  DedicatedEndpoints = `${friendliBaseUrl}/products/dedicated-endpoints`,
  Container = `${friendliBaseUrl}/products/container`,

  SupportedModels = `${friendliBaseUrl}/models`,
}

const docsBaseUrl = 'https://docs.friendli.ai';

const enum DocsUrls {
  Default = docsBaseUrl,
  RunFriendliContainer = `${docsBaseUrl}/guides/container/running_friendli_container`,
  ServerlessEndpointsIntro = `${docsBaseUrl}/guides/serverless_endpoints/intro`,
  ServerlessEndpointsRateLimit = `${docsBaseUrl}/guides/serverless_endpoints/rate_limit`,
  ServerlessEndpointsBuiltInTools = `${docsBaseUrl}/guides/serverless_endpoints/tools/built_in_tools`,
  DedicatedEndpointsIntro = `${docsBaseUrl}/guides/dedicated_endpoints/intro`,
  DedicatedEndpointsQuickstart = `${docsBaseUrl}/guides/dedicated_endpoints/quickstart`,
  ContainerIntro = `${docsBaseUrl}/guides/container/intro`,
}

const huggingFaceBaseUrl = 'https://huggingface.co';

const enum HuggingFaceUrls {
  Default = huggingFaceBaseUrl,
  accessToken = `${huggingFaceBaseUrl}/settings/tokens`,
}

const wandbBaseUrl = 'https://wandb.ai';

const enum WandbUrls {
  Default = wandbBaseUrl,
  apiKey = `${wandbBaseUrl}/settings#api`,
}

const InferenceUrls = {
  dev: 'https://inference-dev.friendli.ai',
  staging: 'https://inference-staging.friendli.ai',
  prod: 'https://inference.friendli.ai',
};

export {
  ApiPaths,
  getApiEnv,
  isApiPath,
  serverUrls,
  InferenceUrls,
  FriendliWebsiteUrls,
  DocsUrls,
  HuggingFaceUrls,
  WandbUrls,
};
